import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiCall } from '@/utils/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft,
  User,
  FileText,
  Clock,
  CheckCircle,
  Mail,
  AlertCircle,
  ArrowRight,
  Filter,
  Trophy,
  Target,
} from 'lucide-react';
import LeadDetailDialog from '@/components/kommo/LeadDetailDialog';
import {
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  Legend,
} from 'recharts';

interface RecentLead {
  kommo_id: number;
  name: string | null;
  price: string;
  status_id: number;
  status_name?: string;
  kommo_created_at: string;
  is_deleted: boolean;
}

interface ActivityDay {
  day: string;
  count: number;
}

interface SellerPerformance {
  seller_name: string;
  kommo_user_id: number;
  total_leads: number;
  won_count: number;
  conversion_rate: number;
  leads_per_month: Array<{ month: string; count: number }>;
  won_per_month: Array<{ month: string; count: number }>;
  events_breakdown: Array<{ type: string; count: number }>;
  avg_first_action_hours: number | null;
  response_rate: number;
  responded_leads: number;
  recent_leads_count: number;
  recent_leads: RecentLead[];
  activity_per_day: ActivityDay[];
  status_map?: Record<string, string>;
}

interface SellerEvent {
  type: string;
  entity_id: number;
  lead_name: string | null;
  timestamp: string;
  detail: string | null;
  data: any;
}

interface EventType {
  type: string;
  count: number;
}

interface SellerEventsData {
  seller_name: string;
  events: SellerEvent[];
  event_types: EventType[];
  status_map: Record<string, string>;
  seller_map: Record<string, string>;
  period: { from: string; to: string };
}

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#ef4444', '#06b6d4'];

const EVENT_LABELS: Record<string, string> = {
  lead_status_changed: 'Changement statut',
  entity_responsible_changed: 'Reassignation',
  entity_tag_added: 'Tag ajoute',
  entity_tag_deleted: 'Tag retire',
  common_note_added: 'Note ajoutee',
  common_note_deleted: 'Note supprimee',
  entity_linked: 'Liaison',
  entity_unlinked: 'Deliaison',
  name_field_changed: 'Nom modifie',
  lead_added: 'Lead cree',
  lead_deleted: 'Lead supprime',
  lead_restored: 'Lead restaure',
  outgoing_mail: 'Email envoye',
  incoming_mail: 'Email recu',
  entity_merged: 'Fusion',
  entity_direct_message: 'Message direct',
  service_note_added: 'Note service',
};

const getEventLabel = (type: string): string => {
  if (EVENT_LABELS[type]) return EVENT_LABELS[type];
  if (type.startsWith('custom_field_') && type.endsWith('_value_changed')) {
    return 'Champ modifie';
  }
  return type;
};

const KommoSellerDetail: React.FC = () => {
  const { kommoUserId } = useParams<{ kommoUserId: string }>();
  const { token } = useAuth();
  const [data, setData] = useState<SellerPerformance | null>(null);
  const [loading, setLoading] = useState(true);

  // Lead detail dialog
  const [dialogLeadId, setDialogLeadId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Events state
  const [eventsData, setEventsData] = useState<SellerEventsData | null>(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventType, setEventType] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      if (!kommoUserId) return;
      setLoading(true);
      try {
        const res = await apiCall<{ success: boolean; data: SellerPerformance }>(
          token,
          `/kommo/analytics/sellers/${kommoUserId}/performance`
        );
        if (res.success) setData(res.data);
      } catch (err) {
        toast.error('Erreur lors du chargement des performances');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [kommoUserId, token]);

  const fetchEvents = useCallback(async () => {
    if (!kommoUserId) return;
    setEventsLoading(true);
    try {
      const params = new URLSearchParams();
      if (eventType) params.set('type', eventType);
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);

      const res = await apiCall<{ success: boolean; data: SellerEventsData }>(
        token,
        `/kommo/analytics/sellers/${kommoUserId}/events?${params.toString()}`
      );
      if (res.success) setEventsData(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des evenements');
    } finally {
      setEventsLoading(false);
    }
  }, [token, kommoUserId, eventType, fromDate, toDate]);

  useEffect(() => {
    if (kommoUserId) fetchEvents();
  }, [fetchEvents, kommoUserId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="px-4 py-4">
        <Card className="border-red-500 bg-red-50">
          <CardHeader>
            <div className="flex items-center text-red-600">
              <AlertCircle className="h-6 w-6 mr-2" />
              <CardTitle className="text-red-700">Commercial non trouve</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline">
              <Link to="/kommo/sellers">
                <ArrowLeft className="h-4 w-4 mr-2" /> Retour
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const kpis = [
    { title: 'Total leads', value: data.total_leads.toLocaleString('fr-FR'), icon: FileText, color: 'text-blue-600' },
    { title: 'Leads convertis', value: data.won_count.toLocaleString('fr-FR'), icon: Trophy, color: 'text-emerald-600' },
    { title: 'Taux de conversion', value: `${data.conversion_rate}%`, icon: Target, color: 'text-violet-600' },
    { title: 'Temps moyen 1ere action', value: data.avg_first_action_hours !== null ? `${data.avg_first_action_hours}h` : 'N/A', icon: Clock, color: 'text-amber-600' },
    { title: 'Taux de reponse (24h)', value: `${data.response_rate}%`, icon: CheckCircle, color: 'text-emerald-600' },
    { title: 'Leads avec reponse', value: `${data.responded_leads} / ${data.recent_leads_count}`, icon: Mail, color: 'text-slate-600' },
  ];

  const openLeadDialog = (kommoId: number) => {
    setDialogLeadId(kommoId);
    setDialogOpen(true);
  };

  return (
    <div className="px-4 py-4 space-y-6">
      {/* Lead detail dialog */}
      <LeadDetailDialog
        kommoId={dialogLeadId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white p-5 rounded-xl border border-slate-200">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/kommo/sellers">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900">{data.seller_name}</h1>
              <p className="text-sm text-slate-500 font-medium">ID Kommo: {data.kommo_user_id}</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.title} className="border-slate-200 hover:shadow transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  {kpi.title}
                </CardTitle>
                <div className={cn('p-1.5 rounded-md bg-slate-50 border border-slate-100', kpi.color)}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold tracking-tight text-slate-900">{kpi.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Leads & Won per month */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-bold text-slate-900">Leads & Conversions par mois</CardTitle>
            <CardDescription className="text-xs text-slate-500">Tendance d'attribution et de conversion</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={(() => {
                  const wonMap = new Map((data.won_per_month || []).map((w) => [w.month, w.count]));
                  return data.leads_per_month.map((m) => ({
                    month: m.month,
                    leads: m.count,
                    won: wonMap.get(m.month) || 0,
                  }));
                })()}>
                  <defs>
                    <linearGradient id="sellerLeadsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                  <Legend />
                  <Area type="monotone" dataKey="leads" stroke="#3b82f6" strokeWidth={2} fill="url(#sellerLeadsGrad)" name="Leads" />
                  <Bar dataKey="won" fill="#22c55e" radius={[3, 3, 0, 0]} name="Convertis" barSize={16} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Events breakdown - horizontal bars */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-bold text-slate-900">Repartition des evenements</CardTitle>
            <CardDescription className="text-xs text-slate-500">Types d'actions effectuees</CardDescription>
          </CardHeader>
          <CardContent>
            {(() => {
              const maxEvt = Math.max(...data.events_breakdown.map((e) => e.count), 1);
              return (
                <div className="space-y-3">
                  {data.events_breakdown.map((evt, i) => {
                    const pct = (evt.count / maxEvt) * 100;
                    const color = COLORS[i % COLORS.length];
                    return (
                      <div key={evt.type} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-slate-700">{getEventLabel(evt.type)}</span>
                          <span className="font-mono text-slate-900 font-semibold">
                            {evt.count.toLocaleString('fr-FR')}
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${pct}%`, backgroundColor: color }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Activity heatmap (bar chart per day) */}
      {data.activity_per_day && data.activity_per_day.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-bold text-slate-900">Activite quotidienne</CardTitle>
            <CardDescription className="text-xs text-slate-500">90 derniers jours</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.activity_per_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 9 }}
                    stroke="#94a3b8"
                    tickFormatter={(v) => new Date(v).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                    labelFormatter={(v) => new Date(v).toLocaleDateString('fr-FR', { dateStyle: 'medium' })}
                  />
                  <Bar dataKey="count" fill="#3b82f6" radius={[2, 2, 0, 0]} name="Evenements" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seller Events with filters */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <CardTitle className="text-base font-bold text-slate-900">Evenements</CardTitle>
              <CardDescription className="text-xs text-slate-500">
                {eventsData ? `${eventsData.events.length} evenements` : 'Chargement...'}
                {eventsData ? ` | ${eventsData.period.from} au ${eventsData.period.to}` : ''}
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Filtres:</span>
            </div>
            <SearchableSelect
              value={eventType || 'all'}
              onValueChange={(v) => setEventType(v === 'all' ? '' : v)}
              placeholder="Tous les types"
              className="w-[220px]"
              options={[
                { value: 'all', label: 'Tous les types' },
                ...(eventsData?.event_types.map((et) => ({
                  value: et.type,
                  label: `${getEventLabel(et.type)} (${et.count})`,
                })) || []),
              ]}
            />
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-[160px]"
            />
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-[160px]"
            />
          </div>
          <p className="text-[10px] text-slate-400 mt-2">Periode maximale: 1 mois</p>
        </CardHeader>
        <CardContent className="p-0">
          {eventsLoading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : eventsData && eventsData.events.length > 0 ? (
            <div className="max-h-[500px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Lead</TableHead>
                    <TableHead>Detail</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eventsData.events.map((event, i) => (
                    <TableRow key={`${event.entity_id}-${event.timestamp}-${i}`}>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {getEventLabel(event.type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => openLeadDialog(event.entity_id)}
                          className="text-primary hover:underline text-sm text-left"
                        >
                          {event.lead_name || `#${event.entity_id}`}
                        </button>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-[250px] truncate">
                        {event.detail || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(event.timestamp).toLocaleString('fr-FR', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 text-sm">
              Aucun evenement trouve pour cette periode
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent leads */}
      {data.recent_leads && data.recent_leads.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-bold text-slate-900">Leads recents</CardTitle>
            <CardDescription className="text-xs text-slate-500">20 derniers leads assignes</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead className="text-right">Prix</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent_leads.map((lead) => (
                  <TableRow key={lead.kommo_id} className="hover:bg-slate-50/50 cursor-pointer" onClick={() => openLeadDialog(lead.kommo_id)}>
                    <TableCell className="font-mono text-primary">#{lead.kommo_id}</TableCell>
                    <TableCell className="font-medium">
                      {lead.name || 'Sans nom'}
                      {lead.is_deleted && (
                        <Badge variant="destructive" className="ml-2 text-[10px]">Supprime</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {lead.price ? `${parseFloat(lead.price).toLocaleString('fr-FR')} €` : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {lead.status_name || data.status_map?.[String(lead.status_id)] || `#${lead.status_id}`}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(lead.kommo_created_at).toLocaleDateString('fr-FR', { dateStyle: 'medium' })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openLeadDialog(lead.kommo_id)}>
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default KommoSellerDetail;
